import { useState, useCallback, useEffect } from "react";
import useDebounce from "./useDebounce";
import { useDisclosure } from "@heroui/react";
import axios from "axios";
import { BASE_API } from "@/constants";
import { showToast } from "@/utils";
import { useSearchParams } from "react-router-dom";

/**
 * Custom hook to handle common data table functionalities with URL persistence.
 *
 * @param {string} endpoint - The API endpoint for fetching data (e.g., "inventory/material-categories/data")
 * @param {string} deleteEndpointSrc - The API endpoint pattern for deleting (e.g., "inventory/material-categories/{id}/delete")
 * @param {object} baseParams - Additional base parameters for fetching data
 * @param {object} initialFilters - Additional filters structure and default values
 */
const useDataTable = (
    endpoint,
    deleteEndpointSrc = "",
    baseParams = {},
    initialFilters = {},
) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Helper to get initial value from URL or default
    const getInitialNum = (key, def) => {
        const val = searchParams.get(key);
        return val ? Number(val) : def;
    };

    const getInitialString = (key, def) => {
        return searchParams.get(key) || def;
    };

    // Pagination & Filter State (Initialized from URL)
    const [page, setPage] = useState(getInitialNum("page", 1));
    const [perPage, setPerPage] = useState(getInitialNum("per_page", 10));
    const [filterValue, setFilterValue] = useState(
        getInitialString("search", ""),
    );

    // Additional filters state (Initialized from URL if present, else defaults)
    const [filters, setFilters] = useState(() => {
        const newFilters = { ...initialFilters };
        Object.keys(newFilters).forEach((key) => {
            const val = searchParams.get(key);
            if (val !== null) newFilters[key] = val;
        });
        return newFilters;
    });

    // Pending filters state for filter panel
    const [pendingFilters, setPendingFilters] = useState({});
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

    // Debounce search
    const debouncedFilter = useDebounce(filterValue, 500);

    // Sync State to URL
    useEffect(() => {
        const newParams = {};

        if (page > 1) newParams.page = String(page);
        if (perPage !== 10) newParams.per_page = String(perPage);

        if (debouncedFilter) newParams.search = debouncedFilter;

        // Merge filters
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            // Skip null, undefined, empty string, and empty arrays
            if (
                value === null ||
                value === undefined ||
                value === "" ||
                (Array.isArray(value) && value.length === 0)
            ) {
                return;
            }
            // Convert arrays to comma-separated string
            if (Array.isArray(value)) {
                newParams[key] = value.join(",");
            } else {
                newParams[key] = String(value);
            }
        });

        // Use replace to prevent history spamming while typing (since we track debounced, it's chunks, but still)
        // Ideally: replace if search/typing, push if page change?
        // For simplicity: setSearchParams(newParams, { replace: true });
        // But if filtering changes results significantly, user might want to go back.
        // Let's use standard set which pushes by default unless we specify.
        setSearchParams(newParams, { replace: true });
    }, [page, perPage, debouncedFilter, filters, setSearchParams]);

    // Sync pending filters when filter panel opens
    useEffect(() => {
        if (isFilterPanelOpen) {
            setPendingFilters({ ...filters });
        }
    }, [isFilterPanelOpen]);

    // Data State
    const [apiResponse, setApiResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch Data
    const params = {
        page,
        per_page: perPage,
        search: debouncedFilter,
        ...filters, // Include additional filters in params
        ...baseParams,
    };

    const paramsString = JSON.stringify(params);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${BASE_API}/${endpoint}`, {
                params,
            });
            // We expect the response.data to be the Paginator object
            setApiResponse(response.data.data);
        } catch (err) {
            setError(err);
            console.error("Error fetching data:", err);
        } finally {
            setIsLoading(false);
        }
    }, [endpoint, paramsString]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const items = apiResponse?.data || [];
    const meta = {
        currentPage: apiResponse?.current_page || 1,
        lastPage: apiResponse?.last_page || 1,
        total: apiResponse?.total || 0,
        from: apiResponse?.from || 0,
        to: apiResponse?.to || 0,
    };

    // Count active filters (only from initialFilters, not search or pagination)
    const activeFilterCount = Object.keys(filters).filter((key) => {
        const value = filters[key];
        // Skip empty values
        if (value === "" || value === null || value === undefined) {
            return false;
        }
        // Skip empty arrays
        if (Array.isArray(value) && value.length === 0) {
            return false;
        }
        return true;
    }).length;

    // Handlers
    const onSearchChange = useCallback((value) => {
        setFilterValue(value);
        setPage(1);
    }, []);

    const onPerPageChange = useCallback((e) => {
        setPerPage(Number(e.target.value));
        setPage(1);
    }, []);

    const setFilter = useCallback((key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(initialFilters);
        setFilterValue("");
        setPage(1);
    }, [initialFilters]);

    // Pending filter handlers for filter panel
    const updatePendingFilter = useCallback((key, value) => {
        setPendingFilters((prev) => ({ ...prev, [key]: value }));
    }, []);

    const applyPendingFilters = useCallback(() => {
        Object.keys(pendingFilters).forEach((key) => {
            const value = pendingFilters[key];
            // Convert array to comma-separated string for URL params
            if (Array.isArray(value)) {
                setFilter(key, value.join(","));
            } else {
                setFilter(key, value || "");
            }
        });
    }, [pendingFilters, setFilter]);

    const clearPendingFilters = useCallback(() => {
        setPendingFilters(initialFilters);
        resetFilters();
    }, [initialFilters, resetFilters]);

    // Delete Logic
    const deleteModal = useDisclosure();
    const [deleteId, setDeleteId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const openDeleteModal = (id) => {
        setDeleteId(id);
        deleteModal.onOpen();
    };

    const confirmDelete = async () => {
        if (!deleteId || !deleteEndpointSrc) return;

        setIsDeleting(true);
        try {
            const url = `${BASE_API}/${deleteEndpointSrc.replace("{id}", deleteId)}`;
            await axios.delete(url);

            showToast("success", "Berhasil", "Data berhasil dihapus.");
            fetchData(); // Refresh list
            deleteModal.onClose();
        } catch (err) {
            console.error("Delete error:", err);
            let msg =
                "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi atau hubungi administrator.";

            if (err.response?.status === 409 || err.response?.status === 422) {
                msg =
                    "Data tidak dapat dihapus karena sedang digunakan oleh data lain.";
            }

            showToast("error", "Gagal", msg);
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        // Data state
        items,
        meta,
        apiResponse,
        isLoading,
        error,
        refetch: fetchData,

        // Search
        search: {
            value: filterValue,
            onChange: onSearchChange,
        },

        // Pagination
        pagination: {
            page,
            setPage,
            perPage,
            onPerPageChange,
        },

        // Applied filters
        filters: {
            values: filters,
            setFilter,
            reset: resetFilters,
            activeCount: activeFilterCount,
        },

        // Filter panel state
        filterPanel: {
            isOpen: isFilterPanelOpen,
            setIsOpen: setIsFilterPanelOpen,
            pending: pendingFilters,
            setPending: setPendingFilters,
            updatePending: updatePendingFilter,
            apply: applyPendingFilters,
            clear: clearPendingFilters,
        },

        // Delete state/handlers
        deleteState: {
            isOpen: deleteModal.isOpen,
            onOpenChange: deleteModal.onOpenChange,
            onClose: deleteModal.onClose,
            deleteId,
            isDeleting,
            openDeleteModal,
            confirmDelete,
        },
    };
};

export default useDataTable;
